import type { CreatePublicationRequest } from '@/api/ApiClient';
import { removeLeadingTrailingBrTags, sanitizeRichTextAllowlist } from '@/lib/utils/textUtils';

export type ImportedPublicationDraft = Pick<
  CreatePublicationRequest,
  'title' | 'year' | 'authors' | 'journal' | 'cite' | 'doi' | 'description'
>;

export function isBibTexFormat(content: string): boolean {
  return /\s*@article/i.test(content);
}

export function isRisCitation(content: string): boolean {
  return /\s*ER\s+-/i.test(content);
}

export function parseImportedCitation(content: string): ImportedPublicationDraft {
  const trimmed = content.trim();
  if (!trimmed) throw new Error('No citation content provided.');

  if (isBibTexFormat(trimmed)) {
    return parseBibTex(trimmed);
  }
  if (isRisCitation(trimmed)) {
    return parseRis(trimmed);
  }
  return parse2dccCitation(trimmed);
}

export function buildCitationFromFields(input: {
  title?: string;
  authors?: string;
  journal?: string;
  year?: number;
  doi?: string;
}): string {
  const authors = normalizeCitationText(input.authors ?? '');
  const title = normalizeCitationText(input.title ?? '');
  const journal = normalizeCitationText(input.journal ?? '');
  const year = input.year ? String(input.year) : '';
  const doi = sanitizeDoi(input.doi ?? '');
  const titlePart = title ? `"${title},"` : '';
  const journalPart = journal ? `<i>${journal}</i>` : '';
  const yearPart = year ? `(${year})` : '';
  const doiPart = doi ? `${doi}.` : '';
  return [authors, titlePart, journalPart, yearPart + '.', doiPart]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeCitationText(value: string): string {
  const withoutEdgeBreaks = removeLeadingTrailingBrTags(value ?? '');
  const sanitized = sanitizeRichTextAllowlist(withoutEdgeBreaks, ['b', 'i', 'em', 'strong', 'u', 'sub', 'sup']);
  return sanitized
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseBibTex(content: string): ImportedPublicationDraft {
  let title = extract('title', content);
  if (title) {
    title = title.replace(/–/g, '-');
    title = title.replace(/{,}/g, ',');
  }
  const journal = extract('journal', content);
  const year = extractNumber('year', content) ?? undefined;
  const volume = extractVolumeInfo(content);
  const doi = sanitizeDoi(extract('doi', content) ?? '');
  const authors = (extract('author', content) ?? '')
    .split(' and ')
    .map((a) => formatAuthor(a))
    .filter(Boolean)
    .join(', ');
  const cite = `${authors}, "${title ?? ''}," <i>${journal ?? ''}</i> ${volume}(${year ?? ''}). ${doi}.`
    .replace(/\s+/g, ' ')
    .trim();
  return {
    title: title ?? '',
    year,
    authors,
    journal: journal ?? '',
    cite,
    doi,
    description: '',
  };
}

function parseRis(content: string): ImportedPublicationDraft {
  const lines = content.split(/\r?\n/);
  const byTag = new Map<string, string[]>();
  for (const raw of lines) {
    const m = raw.match(/^([A-Z0-9]{2})\s*-\s*(.*)$/);
    if (!m) continue;
    const tag = m[1];
    const value = m[2].trim();
    const arr = byTag.get(tag) ?? [];
    arr.push(value);
    byTag.set(tag, arr);
  }
  const title = first(byTag, ['TI', 'T1']) ?? '';
  const journal = first(byTag, ['JO', 'JF', 'JA']) ?? '';
  const doi = sanitizeDoi(first(byTag, ['DO']) ?? '');
  const year = parseYear(first(byTag, ['PY', 'Y1']) ?? '');
  const authors = (byTag.get('AU') ?? [])
    .map((a) => formatAuthor(a))
    .filter(Boolean)
    .join(', ');
  return {
    title,
    year,
    authors,
    journal,
    cite: buildCitationFromFields({ title, authors, journal, year, doi }),
    doi,
    description: '',
  };
}

function parse2dccCitation(content: string): ImportedPublicationDraft {
  const normalized = content.replace(/\s+/g, ' ').trim();

  const authors = (normalized.match(/^(.*?)\s*["“]/)?.[1] ?? '').trim().replace(/,\s*$/, '');
  let title = (normalized.match(/["“]\s*(.*?)\s*["”]/)?.[1] ?? '').trim();
  if (title.endsWith(',')) title = title.slice(0, -1).trim();

  let journal = (normalized.match(/<i>\s*(.*?)\s*<\/i>/i)?.[1] ?? '').trim();
  if (!journal) {
    journal = (normalized.match(/["”]\s*,?\s*([^<(]+?)\s*\(\d{4}\)\.?/i)?.[1] ?? '').trim();
  }
  journal = journal.replace(/^,\s*/, '').trim();

  const yearMatch = normalized.match(/\((\d{4})\)\.?/);
  const year = yearMatch ? parseInt(yearMatch[1], 10) : undefined;

  const doiMatch = normalized.match(/\b(?:https?:\/\/doi\.org\/)?(10\.\S+?)(?=[\s<]|$)/i);
  const doi = sanitizeDoi((doiMatch?.[1] ?? '').replace(/[.,;:]$/, ''));

  const doiEndIndex = doiMatch?.index !== undefined ? doiMatch.index + doiMatch[0].length : -1;
  let cite = doiEndIndex > -1 ? normalized.slice(0, doiEndIndex).trim() : normalized;
  if (!cite.endsWith('.')) cite += '.';
  const description = doiEndIndex > -1 ? normalized.slice(doiEndIndex).trim() : '';
  return {
    title,
    year: Number.isNaN(year) ? undefined : year,
    authors,
    journal,
    cite,
    doi: sanitizeDoi(doi),
    description,
  };
}

function extract(key: string, content: string): string | null {
  const regexForDelimiter = new RegExp(`${key}\\s*=\\s*(.)`, 'i');
  const delimMatch = content.match(regexForDelimiter);
  const startDelim = delimMatch?.[1] ?? '"';
  const endDelim = startDelim === '{' ? '}' : '"';
  const regex = new RegExp(`${key}\\s*=\\s*${startDelim}([^${endDelim}]*)${endDelim}`, 'i');
  const match = content.match(regex);
  return match?.[1] ?? null;
}

function extractNumber(key: string, content: string): number | null {
  const extracted = extract(key, content);
  return extracted ? parseInt(extracted, 10) : null;
}

function extractVolumeInfo(content: string): string {
  const volume = extractNumber('volume', content);
  if (!volume) return '';
  let issue = extract('issue', content) ?? '';
  if (!issue) issue = extract('number', content) ?? '';
  if (issue) issue = `(${issue})`;
  const pages = extract('pages', content);
  return pages ? `${volume}${issue}, ${pages} ` : `${volume}${issue} `;
}

function formatAuthor(str: string): string {
  const lastcommafirst = str.split(', ');
  if (lastcommafirst.length > 1) {
    const initials = lastcommafirst[1]
      .split(' ')
      .map((first) => first.charAt(0).toUpperCase() + '.')
      .join('');
    return `${initials} ${lastcommafirst[0]}`.trim();
  }
  const names = str.split(' ');
  if (names.length < 2) return str.trim();
  const initials = names
    .slice(0, -1)
    .map((first) => first.charAt(0).toUpperCase() + '.')
    .join('');
  return `${initials} ${names.slice(-1)[0]}`.trim();
}

function sanitizeDoi(value: string): string {
  return value.replace(/^https?:\/\/doi\.org\//i, '').trim();
}

function first(map: Map<string, string[]>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = map.get(key)?.[0];
    if (value) return value;
  }
  return undefined;
}

function parseYear(input: string): number | undefined {
  const m = input.match(/\d{4}/);
  if (!m) return undefined;
  const year = parseInt(m[0], 10);
  return Number.isNaN(year) ? undefined : year;
}
